import { useMemo, useState, type FormEvent } from "react";
import {
  Building2,
  CircleHelp,
  CreditCard,
  Database,
  FileClock,
  Gauge,
  KeyRound,
  LogOut,
  Menu,
  Palette,
  RadioTower,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";

import { Panel } from "@/components/panel";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ErrorState, Loading } from "@/components/empty-state";
import {
  addPlatformSupportCaseNote,
  listAudit,
  loadWorkspace,
  organisationAdminConsoleSnapshot,
  platformSupportWorkspace,
  requestPlatformSupportCase,
  type PlatformSupportSeverity,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import { supabase } from "@/lib/supabase";
import type { Organisation } from "@/lib/types";
import { displayDate, titleCase } from "@/lib/utils";
import { Branding } from "@/screens/branding";
import { Budgets } from "@/screens/budgets";
import { ExportCenter } from "@/screens/export-center";
import { PlanEntitlements } from "@/screens/plan-entitlements";
import { Team } from "@/screens/team";

type Workspace = Awaited<ReturnType<typeof loadWorkspace>>;
type AdminSection =
  | "overview"
  | "people"
  | "structure"
  | "identity"
  | "plan"
  | "security"
  | "integrations"
  | "data"
  | "audit"
  | "support";

const sections: {
  id: AdminSection;
  label: string;
  icon: typeof Gauge;
}[] = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "people", label: "Users & access", icon: Users },
  { id: "structure", label: "Structure & controls", icon: Building2 },
  { id: "identity", label: "Organisation identity", icon: Palette },
  { id: "plan", label: "Plan & billing", icon: CreditCard },
  { id: "security", label: "Security & SSO", icon: KeyRound },
  { id: "integrations", label: "Integrations", icon: RadioTower },
  { id: "data", label: "Data & exports", icon: Database },
  { id: "audit", label: "Admin audit", icon: FileClock },
  { id: "support", label: "Support", icon: CircleHelp },
];

export function AdminConsole({
  workspace,
  organisation,
  organisations,
  setOrganisationId,
  organisationChanged,
}: {
  workspace: Workspace;
  organisation: Organisation;
  organisations: Organisation[];
  setOrganisationId: (id: string) => void;
  organisationChanged: (organisation: Organisation) => void;
}) {
  const [section, setSection] = useState<AdminSection>("overview");
  const [refresh, setRefresh] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const changed = () => setRefresh((value) => value + 1);
  const can = () => true;

  return (
    <div className="min-h-screen bg-[#f5f7f6] text-[#102733]">
      <a
        href="#admin-content"
        className="sr-only z-[100] rounded bg-white px-4 py-2 focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to administration content
      </a>
      <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-[#dce5e1] bg-white px-4 lg:hidden">
        <Button
          aria-label="Open administration navigation"
          variant="ghost"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="size-5" />
        </Button>
        <RaqozLogo />
        <span className="ml-auto text-xs font-bold tracking-[.12em] text-[#0f766e]">
          ADMIN
        </span>
      </header>
      {mobileOpen && (
        <button
          aria-label="Close administration navigation"
          className="fixed inset-0 z-40 bg-slate-950/35 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        aria-label="RAQOZ Admin navigation"
        className={`fixed inset-y-0 left-0 z-50 flex w-[290px] flex-col border-r border-[#dce5e1] bg-[#102733] p-5 text-white transition-transform lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between">
          <RaqozLogo inverse />
          <Button
            aria-label="Close administration navigation"
            className="lg:hidden"
            variant="ghost"
            onClick={() => setMobileOpen(false)}
          >
            <X className="size-5" />
          </Button>
        </div>
        <p className="mt-8 text-[11px] font-black tracking-[.16em] text-emerald-300">
          ORGANISATION CONTROL PLANE
        </p>
        <label className="mt-3 grid gap-2 text-xs font-semibold text-white/70">
          Organisation
          <select
            aria-label="Organisation"
            className="min-h-11 rounded-xl border border-white/15 bg-white/10 px-3 text-sm text-white"
            value={organisation.id}
            onChange={(event) => setOrganisationId(event.target.value)}
          >
            {organisations.map((item) => (
              <option className="text-slate-950" value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <nav className="mt-7 grid gap-1">
          {sections.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={`flex min-h-11 items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold transition ${
                  section === item.id
                    ? "bg-white text-[#102733]"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
                onClick={() => {
                  setSection(item.id);
                  setMobileOpen(false);
                }}
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-auto border-t border-white/10 pt-5">
          <p className="truncate text-sm font-semibold">{workspace.user.name}</p>
          <p className="truncate text-xs text-white/50">{workspace.user.email}</p>
          <Button
            className="mt-3 w-full justify-start text-white hover:bg-white/10"
            variant="ghost"
            onClick={() => void supabase.auth.signOut()}
          >
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </aside>
      <main id="admin-content" className="min-h-screen p-5 lg:ml-[290px] lg:p-8 xl:p-10">
        {section === "overview" && (
          <AdminOverview organisation={organisation} refresh={refresh} navigate={setSection} />
        )}
        {section === "people" && (
          <Team
            organisation={organisation}
            refresh={refresh}
            query=""
            can={can}
            changed={changed}
            currentUserId={workspace.user.id}
          />
        )}
        {section === "structure" && (
          <Budgets
            organisation={organisation}
            refresh={refresh}
            query=""
            can={can}
            changed={changed}
          />
        )}
        {section === "identity" && (
          <Branding
            organisation={organisation}
            can={can}
            onSaved={(updated) => {
              organisationChanged(updated);
              changed();
            }}
          />
        )}
        {section === "plan" && (
          <PlanEntitlements
            organisation={organisation}
            refresh={refresh}
            platformOwner={false}
            changed={changed}
          />
        )}
        {section === "security" && <AdminSecurity organisation={organisation} refresh={refresh} />}
        {section === "integrations" && <AdminIntegrations organisation={organisation} refresh={refresh} navigate={setSection} />}
        {section === "data" && <ExportCenter organisation={organisation} />}
        {section === "audit" && (
          <AdminAudit organisation={organisation} refresh={refresh} />
        )}
        {section === "support" && (
          <AdminSupport organisation={organisation} refresh={refresh} changed={changed} />
        )}
      </main>
    </div>
  );
}

function AdminOverview({
  organisation,
  refresh,
  navigate,
}: {
  organisation: Organisation;
  refresh: number;
  navigate: (section: AdminSection) => void;
}) {
  const { data, error, loading } = useData(
    () => organisationAdminConsoleSnapshot(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  const ready = (data.readiness || []).filter((item: any) => item.ready).length;
  const total = (data.readiness || []).length;
  return (
    <>
      <div className="mb-7">
        <p className="text-xs font-black tracking-[.16em] text-[#0f766e]">RAQOZ ADMIN</p>
        <h1 className="mt-2 font-serif text-4xl tracking-[-.035em]">
          {organisation.name}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          One place to manage people, organisation settings, controls, subscription,
          evidence exports and launch readiness.
        </p>
      </div>
      <section aria-label="Administration summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Active people" value={data.people?.active || 0} detail={`${data.people?.administrators || 0} administrators`} />
        <Metric label="Invitations" value={data.people?.invited || 0} detail={`${data.people?.suspended || 0} suspended`} />
        <Metric label="Locations" value={data.structure?.locations || 0} detail={`${data.structure?.approval_rules || 0} approval controls`} />
        <Metric label="Launch readiness" value={`${ready}/${total}`} detail={ready === total ? "Ready" : "Action required"} />
      </section>
      <section className="mt-6 grid gap-5 xl:grid-cols-[1.2fr_.8fr]">
        <Panel>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-black tracking-[.14em] text-[#0f766e]">READINESS REGISTER</p>
              <h2 className="mt-2 text-2xl font-semibold">Launch controls</h2>
            </div>
            <Badge value={ready === total ? "ready" : "attention"} />
          </div>
          <ul className="mt-5 grid gap-3">
            {(data.readiness || []).map((item: any) => (
              <li className="flex items-start gap-3 rounded-xl border border-slate-200 p-4" key={item.key}>
                <ShieldCheck className={`mt-0.5 size-5 shrink-0 ${item.ready ? "text-emerald-600" : "text-amber-600"}`} />
                <div>
                  <p className="font-semibold">{item.label}</p>
                  <p className="text-sm text-slate-500">
                    {item.ready ? "Verified" : "Administrator action required"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel>
          <p className="text-xs font-black tracking-[.14em] text-[#0f766e]">SERVICE POSITION</p>
          <h2 className="mt-2 text-2xl font-semibold">{data.subscription?.plan_name || "No plan"}</h2>
          <dl className="mt-5 grid gap-3 text-sm">
            <Fact label="Subscription" value={titleCase(data.subscription?.status || "not configured")} />
            <Fact label="Failed deliveries" value={String(data.integrations?.failed_events || 0)} />
            <Fact label="Open support cases" value={String(data.support?.open || 0)} />
            <Fact label="Audit events (24h)" value={String(data.audit?.events_24h || 0)} />
          </dl>
          <div className="mt-6 grid gap-2">
            <Button onClick={() => navigate("people")}>Manage users & access</Button>
            <Button variant="outline" onClick={() => navigate("support")}>Open support</Button>
          </div>
        </Panel>
      </section>
    </>
  );
}

function AdminSecurity({
  organisation,
  refresh,
}: {
  organisation: Organisation;
  refresh: number;
}) {
  const { data, error, loading } = useData(
    () => organisationAdminConsoleSnapshot(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  return (
    <>
      <p className="text-xs font-black tracking-[.16em] text-[#0f766e]">
        IDENTITY & AUTHENTICATION
      </p>
      <h1 className="mt-2 font-serif text-4xl">Security & SSO</h1>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
        RAQOZ uses one governed identity per person. Provider secrets remain in the
        existing Supabase configuration and are never exposed to browser code.
      </p>
      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <Panel>
          <h2 className="text-xl font-semibold">Authentication methods</h2>
          <ul className="mt-5 grid gap-3">
            <SecurityItem title="Password and recovery" detail="Available through the verified work email and single-use recovery link." />
            <SecurityItem title="Email magic link" detail="Available without creating a second identity." />
            <SecurityItem title="Google OAuth" detail="Uses the existing approved Supabase provider and callback allow-list." />
            <SecurityItem title="Organisation SSO" detail="Discovered from the work-email domain when an approved SSO connection exists." />
          </ul>
        </Panel>
        <Panel>
          <h2 className="text-xl font-semibold">Access posture</h2>
          <dl className="mt-5 grid gap-3 text-sm">
            <Fact label="Active administrators" value={String(data.people?.administrators || 0)} />
            <Fact label="Suspended identities" value={String(data.people?.suspended || 0)} />
            <Fact label="Pending invitations" value={String(data.people?.invited || 0)} />
            <Fact label="Tenant host" value={`${organisation.slug}.raqoz.com`} />
          </dl>
          <p className="mt-5 rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            Passwords, OAuth secrets and SSO certificates are not editable in RAQOZ Admin.
            They remain protected deployment configuration. User access changes happen in
            Users & access and require an audited reason.
          </p>
        </Panel>
      </div>
    </>
  );
}

function AdminIntegrations({
  organisation,
  refresh,
  navigate,
}: {
  organisation: Organisation;
  refresh: number;
  navigate: (section: AdminSection) => void;
}) {
  const { data, error, loading } = useData(
    () => organisationAdminConsoleSnapshot(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const failed = Number(data.integrations?.failed_events || 0);
  return (
    <>
      <p className="text-xs font-black tracking-[.16em] text-[#0f766e]">
        CONNECTED OPERATIONS
      </p>
      <h1 className="mt-2 font-serif text-4xl">Integrations</h1>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
        Monitor delivery health here. Configure accounting exports and governed
        downstream handoffs in their existing RAQOZ workspace modules.
      </p>
      <section className="mt-6 grid gap-4 sm:grid-cols-3">
        <Metric label="Active webhooks" value={data.integrations?.active_webhooks || 0} detail="Enabled endpoints" />
        <Metric label="Pending delivery" value={data.integrations?.pending_events || 0} detail="Pending or processing" />
        <Metric label="Failed delivery" value={failed} detail={failed ? "Support action required" : "Healthy"} />
      </section>
      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <Panel>
          <h2 className="text-xl font-semibold">Accounting bridge</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Export governed accounting evidence to the supported provider format.
            RAQOZ does not execute bank payments.
          </p>
          <Button className="mt-5" asChild>
            <a href="https://raqoz.com/app?view=accounting">Open accounting bridge</a>
          </Button>
        </Panel>
        <Panel>
          <h2 className="text-xl font-semibold">Delivery exceptions</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Failed or dead-letter events are visible to Mission Control as aggregate
            operational evidence. Customer data is not copied into the support case.
          </p>
          <Button className="mt-5" variant="outline" onClick={() => navigate("support")}>
            {failed ? "Open support case" : "View support"}
          </Button>
        </Panel>
      </div>
    </>
  );
}

function SecurityItem({ title, detail }: { title: string; detail: string }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
      <ShieldCheck className="mt-0.5 size-5 shrink-0 text-emerald-600" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-1 text-sm leading-6 text-slate-500">{detail}</p>
      </div>
    </li>
  );
}

function AdminAudit({
  organisation,
  refresh,
}: {
  organisation: Organisation;
  refresh: number;
}) {
  const { data, error, loading } = useData(
    () => listAudit(organisation.id, 250),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  return (
    <>
      <p className="text-xs font-black tracking-[.16em] text-[#0f766e]">ADMIN EVIDENCE</p>
      <h1 className="mt-2 font-serif text-4xl">Audit trail</h1>
      <p className="mt-2 text-sm text-slate-600">
        Immutable organisation actions, actors and reasons. Procurement evidence remains in its workflow context.
      </p>
      <div className="mt-6 grid gap-3">
        {data.map((event: any) => (
          <article className="rounded-2xl border border-slate-200 bg-white p-5" key={event.id}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <strong>{titleCase(event.action)}</strong>
              <time className="text-xs text-slate-500">{displayDate(event.created_at)}</time>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              {event.profiles?.full_name || "System"} · {titleCase(event.entity_type)}
            </p>
            {(event.reason || event.metadata?.reason) && (
              <p className="mt-2 text-sm">{event.reason || event.metadata.reason}</p>
            )}
          </article>
        ))}
      </div>
    </>
  );
}

function AdminSupport({
  organisation,
  refresh,
  changed,
}: {
  organisation: Organisation;
  refresh: number;
  changed: () => void;
}) {
  const { data, error, loading } = useData(
    () => platformSupportWorkspace(organisation.id),
    [organisation.id, refresh],
  );
  const [category, setCategory] = useState("configuration");
  const [severity, setSeverity] = useState<PlatformSupportSeverity>("normal");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [noteCase, setNoteCase] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const eventsByCase = useMemo(() => {
    const result = new Map<string, any[]>();
    for (const event of data?.events || [])
      result.set(event.case_id, [...(result.get(event.case_id) || []), event]);
    return result;
  }, [data]);

  async function createCase(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      await requestPlatformSupportCase(organisation.id, {
        category,
        severity,
        subject,
        description,
      });
      setSubject("");
      setDescription("");
      changed();
    } catch (reason) {
      setFormError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addNote(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      await addPlatformSupportCaseNote(noteCase, note);
      setNoteCase("");
      setNote("");
      changed();
    } catch (reason) {
      setFormError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  return (
    <>
      <p className="text-xs font-black tracking-[.16em] text-[#0f766e]">GOVERNED SUPPORT</p>
      <h1 className="mt-2 font-serif text-4xl">Support cases</h1>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
        Request operational assistance with no tenant impersonation and without transmitting procurement records.
        Every update is retained as immutable case evidence.
      </p>
      <div className="mt-6 grid gap-5 xl:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <h2 className="text-xl font-semibold">Open a case</h2>
          <form className="mt-5 grid gap-4" onSubmit={createCase}>
            <Label>
              Category
              <select className="min-h-11 rounded-xl border border-slate-300 bg-white px-3" value={category} onChange={(event) => setCategory(event.target.value)}>
                {["access","configuration","billing","integration","data_export","security","incident","other"].map((item) => <option value={item} key={item}>{titleCase(item)}</option>)}
              </select>
            </Label>
            <Label>
              Severity
              <select className="min-h-11 rounded-xl border border-slate-300 bg-white px-3" value={severity} onChange={(event) => setSeverity(event.target.value as PlatformSupportSeverity)}>
                {["low","normal","high","critical"].map((item) => <option value={item} key={item}>{titleCase(item)}</option>)}
              </select>
            </Label>
            <Label>Subject<Input value={subject} onChange={(event) => setSubject(event.target.value)} minLength={5} maxLength={160} required /></Label>
            <Label>Description<Textarea value={description} onChange={(event) => setDescription(event.target.value)} minLength={20} maxLength={4000} required /></Label>
            {formError && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{formError}</p>}
            <Button disabled={busy} type="submit">{busy ? "Submitting…" : "Create support case"}</Button>
          </form>
        </Panel>
        <div className="grid content-start gap-4">
          {(data.cases || []).length === 0 && (
            <Panel><h2 className="font-semibold">No support cases</h2><p className="mt-2 text-sm text-slate-500">Your organisation has no recorded support work.</p></Panel>
          )}
          {(data.cases || []).map((item: any) => (
            <article className="rounded-2xl border border-slate-200 bg-white p-5" key={item.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><p className="text-xs font-bold tracking-[.12em] text-slate-500">{titleCase(item.category)}</p><h2 className="mt-1 text-lg font-semibold">{item.subject}</h2></div>
                <div className="flex gap-2"><Badge value={item.severity} /><Badge value={item.status} /></div>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">{item.description}</p>
              {item.resolution && <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900"><strong>Resolution:</strong> {item.resolution}</p>}
              <p className="mt-3 text-xs text-slate-500">{(eventsByCase.get(item.id) || []).length} evidence events · opened {displayDate(item.created_at)}</p>
              {!["resolved","closed"].includes(item.status) && (
                <Button className="mt-4" variant="outline" onClick={() => setNoteCase(item.id)}>Add update</Button>
              )}
            </article>
          ))}
        </div>
      </div>
      {noteCase && (
        <form onSubmit={addNote} className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
          <Label>Case update<Textarea value={note} onChange={(event) => setNote(event.target.value)} minLength={8} required /></Label>
          <div className="mt-4 flex gap-2"><Button disabled={busy} type="submit">Add immutable update</Button><Button type="button" variant="outline" onClick={() => setNoteCase("")}>Cancel</Button></div>
        </form>
      )}
    </>
  );
}

function Metric({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return <Panel><p className="text-xs font-bold tracking-[.12em] text-slate-500">{label.toUpperCase()}</p><p className="mt-2 text-3xl font-semibold">{value}</p><p className="mt-1 text-sm text-slate-500">{detail}</p></Panel>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3"><dt className="text-slate-500">{label}</dt><dd className="text-right font-semibold">{value}</dd></div>;
}
